import { useSyncExternalStore } from "react";
import { NotificationsInboxApi } from "notifications/api/notifications.api";

// Unread count of the caller's notifications-api inbox, shared by everything
// that shows it (the AFMS Command Center bell, the sidebar's "My Inbox"
// badge). One module-level store and one poller, however many components are
// mounted: the sidebar and the page are on screen together, and two pollers
// would drift apart and briefly disagree.
//
// 30s, the same rhythm as the backoffice navbar bell. Best-effort like that
// bell too: notifications-api may not be running everywhere, so a failed
// fetch reads as 0 rather than surfacing an error.
const POLL_MS = 30000;

let unread = 0;
let timer = null;
// Only the newest request may write the count. A poll already in flight when
// the inbox marks something read would otherwise land after the refresh and
// put the old number back.
let latestRequest = 0;
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

/**
 * Re-reads the count now — call after anything that changes it (mark read,
 * archive, delete) so the badges drop without waiting for the next poll.
 * A no-op while nothing displays the count: the next subscriber fetches on
 * mount anyway.
 */
export const refreshInboxUnreadCount = () => {
  if (listeners.size === 0) return Promise.resolve();
  latestRequest += 1;
  const request = latestRequest;
  return NotificationsInboxApi.count()
    .then((res) => res?.data?.data?.unread ?? 0)
    .catch(() => 0)
    .then((next) => {
      if (request !== latestRequest || next === unread) return;
      unread = next;
      emit();
    });
};

const subscribe = (listener) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    refreshInboxUnreadCount();
    timer = setInterval(refreshInboxUnreadCount, POLL_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      clearInterval(timer);
      timer = null;
      // Nothing shows it any more (signed out, or left the portal). Drop the
      // number and any answer still in flight, so the next sign-in on this
      // tab never starts from someone else's count.
      latestRequest += 1;
      unread = 0;
    }
  };
};

const subscribeNothing = () => () => {};
const getUnread = () => unread;
const getNothing = () => 0;

/**
 * @param {boolean} [enabled=true] false reads 0 and does not poll — for a
 *   component that only sometimes shows the count.
 * @returns {number} unread inbox items (not archived, not read)
 */
export default function useInboxUnreadCount(enabled = true) {
  return useSyncExternalStore(
    enabled ? subscribe : subscribeNothing,
    enabled ? getUnread : getNothing
  );
}
